import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../ThemeContext";

const FIELDS = [
  {
    key: "pageBg",
    label: "Fondo base",
    hint: "Color base en zonas oscuras / fondos generales"
  },
  {
    key: "pageFg",
    label: "Texto principal",
    hint: "Texto normal en la mayoría de pantallas"
  },
  {
    key: "cardBg",
    label: "Fondos de tarjetas / paneles",
    hint: "Tarjetas, bloques de contenido, contenedores"
  },
  {
    key: "cardFg",
    label: "Texto en tarjetas",
    hint: "Texto dentro de tarjetas y paneles"
  },
  {
    key: "mutedBg",
    label: "Fondos suaves",
    hint: "Fondos de chips, pills y zonas secundarias"
  },
  {
    key: "mutedBg2",
    label: "Fondos suaves 2",
    hint: "Variación secundaria para fondos suaves"
  },
  {
    key: "border",
    label: "Bordes y contornos",
    hint: "Bordes de tarjetas, pills, inputs..."
  },
  {
    key: "mutedText",
    label: "Texto secundario",
    hint: "Descripciones, textos en gris, subtítulos"
  },
  {
    key: "accentBg",
    label: "Botones / acentos",
    hint: "Color de botones principales y elementos destacados"
  },
  {
    key: "accentFg",
    label: "Texto sobre acento",
    hint: "Texto que va encima del color de acento"
  }
];

export default function ThemeCustomizer() {
  const navigate = useNavigate();
  const { customTheme, setCustomTheme, setTheme } = useTheme();

  const handleChange = (key, value) => {
    setCustomTheme((prev) => ({ ...prev, [key]: value }));
  };

  const handleUseCustom = () => {
    setTheme("custom");
    navigate(-1);
  };

  const resetTheme = () => {
    setCustomTheme({
      pageBg: "#0f172a",
      pageFg: "#f9fafb",
      cardBg: "#111827",
      cardFg: "#e5e7eb",
      mutedBg: "#1f2937",
      mutedBg2: "#374151",
      border: "#4b5563",
      mutedText: "#9ca3af",
      accentBg: "#f97316",
      accentFg: "#111827"
    });
  };

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--page-fg)]">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm mb-4 text-[var(--muted-text)] hover:underline"
        >
          ← Volver
        </button>

        <h1 className="text-2xl font-semibold mb-2">Personalizar tema</h1>
        <p className="text-sm text-[var(--muted-text)] mb-6">
          Ajusta los colores principales. La vista previa se actualiza en tiempo real
          y te ayuda a entender qué está afectando cada control.
        </p>

        <div className="grid gap-8 md:grid-cols-2">
          <div className="space-y-4">
            {FIELDS.map((field) => (
              <div
                key={field.key}
                className="flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium">{field.label}</div>
                  <div className="text-xs text-[var(--muted-text)]">
                    {field.hint}
                  </div>
                  <div className="text-[10px] text-[var(--muted-text)] truncate mt-0.5">
                    {customTheme?.[field.key]}
                  </div>
                </div>
                <input
                  type="color"
                  value={customTheme?.[field.key] ?? "#ffffff"}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  className="w-10 h-10 rounded border border-[var(--border)] bg-transparent cursor-pointer"
                />
              </div>
            ))}

            <div className="pt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleUseCustom}
                className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition"
              >
                Usar este tema
              </button>
              <button
                type="button"
                onClick={resetTheme}
                className="px-4 py-2 rounded-md border border-[var(--border)] text-sm hover:bg-[var(--muted-bg)]"
              >
                Resetear tema personalizado
              </button>
            </div>
          </div>

          <div
            className="rounded-xl border border-[var(--border)] p-6"
            style={{
              backgroundColor: customTheme.cardBg,
              color: customTheme.cardFg
            }}
          >
            <div className="text-xs uppercase tracking-wide mb-2 opacity-70">
              Vista previa
            </div>
            <h2 className="text-xl font-semibold mb-3">
              Paleta personalizada de Flushwars
            </h2>
            <p
              className="text-sm mb-4"
              style={{ color: customTheme.mutedText }}
            >
              Esta tarjeta, el botón y las chips usan tus colores personalizados.
              Ajusta los controles de la izquierda y luego navega por el dashboard
              para ver el efecto real.
            </p>

            <button
              type="button"
              className="px-4 py-2 rounded-md text-sm font-medium"
              style={{
                backgroundColor: customTheme.accentBg,
                color: customTheme.accentFg
              }}
            >
              Botón de acento
            </button>

            <div className="mt-6 flex flex-wrap gap-2">
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs"
                style={{
                  backgroundColor: customTheme.mutedBg,
                  color: customTheme.pageFg,
                  border: `1px solid ${customTheme.border}`
                }}
              >
                Fondos suaves
              </span>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs"
                style={{
                  backgroundColor: customTheme.mutedBg2,
                  color: customTheme.pageFg,
                  border: `1px solid ${customTheme.border}`
                }}
              >
                Fondos suaves 2
              </span>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs"
                style={{
                  backgroundColor: customTheme.pageBg,
                  color: customTheme.pageFg,
                  border: `1px solid ${customTheme.border}`
                }}
              >
                Fondo base
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
